// src/context/ConsentContext.jsx
/**
 * ConsentContext
 * --------------
 * Stores whether the user has granted:
 *  - essential storage (for persistent sign-in)
 *  - analytics storage (future ticket)
 *
 * Note: remembering consent itself is typically treated as essential.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "pdx.consent.v1";

const ConsentContext = createContext(null);

function safeRead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeWrite(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export function ConsentProvider({ children }) {
  const initial = safeRead();

  const [consent, setConsent] = useState(
    initial || { status: "unknown", essential: false, analytics: false },
  );

  const setChoices = useCallback((choices) => {
    const next = {
      status: "set",
      essential: !!choices.essential,
      analytics: !!choices.analytics,
    };
    setConsent(next);
    safeWrite(next);
  }, []);

  const value = useMemo(() => ({ consent, setChoices }), [consent, setChoices]);

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx.consent;
}

export function useConsentActions() {
  const ctx = useContext(ConsentContext);
  if (!ctx)
    throw new Error("useConsentActions must be used within ConsentProvider");
  return { setChoices: ctx.setChoices };
}
